import { useRef } from "react";
import { Key, Tabs as PrimitiveTabs } from "react-aria-components";

import { ThemeCanvas } from "@/theming/themingTypes";

import styles from "./Tabs.module.scss";
import { CustomTabsContext, TabOrientations, TabVariants } from "./Tabs.utils";
import { TabsList, TabsListProps } from "./TabsList";
import { TabsPanel, TabsPanelProps } from "./TabsPanel";
import { TabsTab, TabsTabProps } from "./TabsTab";

export interface TabsProps {
  /**
   * The key of the tab that should be selected by default.
   */
  defaultSelectedTab?: Key;

  /**
   * The key of the currently selected tab.
   */
  selectedTab?: Key;

  /**
   * The orientation of the tabs, either 'horizontal' or 'vertical'.
   */
  orientation?: TabOrientations;

  /**
   * The children to be rendered within the Tabs component.
   */
  children: React.ReactNode;

  /**
   * Canvas to align arrow background wit Section background.
   * Either "default", "lighter" or "darker"
   */
  canvas?: ThemeCanvas;

  /**
   * The orientation of the tabs, either 'default' or 'outlined'.
   */
  variant?: TabVariants;

  /**
   * Callback function that is called when tabs change.
   */
  onSelectionChange?: (key: Key) => void;
}

// Declare the sub-components as part of the Tabs component as we use compound pattern to build the Tabs component
interface TabsComponent extends React.FC<TabsProps> {
  List: React.FC<TabsListProps>;
  Tab: React.FC<TabsTabProps>;
  Panel: React.FC<TabsPanelProps>;
}

export const Tabs = /*#__PURE__*/ (function () {
  const Tabs: TabsComponent = ({
    children,
    defaultSelectedTab,
    selectedTab,
    orientation = TabOrientations.HORIZONTAL,
    variant = TabVariants.DEFAULT,
    onSelectionChange,
  }) => {
    const tabsVariant = orientation === TabOrientations.VERTICAL ? TabVariants.OUTLINED : variant;
    const containerRef = useRef<HTMLDivElement>(null);

    return (
      <CustomTabsContext.Provider
        value={{
          orientation,
          variant: tabsVariant,
        }}
      >
        <PrimitiveTabs
          orientation={orientation}
          defaultSelectedKey={defaultSelectedTab}
          className={styles.tabs}
          selectedKey={selectedTab}
          onSelectionChange={onSelectionChange}
          ref={containerRef}
        >
          {children}
        </PrimitiveTabs>
      </CustomTabsContext.Provider>
    );
  };

  Tabs.List = TabsList;
  Tabs.Tab = TabsTab;
  Tabs.Panel = TabsPanel;

  return Tabs;
})();
